import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Plus, Trash2, Pencil, X, Sparkles, Upload, AlertTriangle, ShieldCheck, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";
import ImageUploadInput from "../../components/ImageUploadInput";

const empty = {
    name: "", qualification: "BHMS", specialization: "Classical Homeopathy", experience_years: 0,
    city: "", area: "", state: "", clinic_name: "", clinic_address: "", phone: "", photo_url: "",
    source_url: "", rating: 4.5, active: true, needs_verification: false,
};

export default function AdminDoctorsInternet() {
    const [items, setItems] = useState([]);
    const [settings, setSettings] = useState({ show_internet_doctors: true });
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [filter, setFilter] = useState("");
    const fileRef = useRef(null);

    const load = async () => {
        const [d, s] = await Promise.all([api.get("/admin/doctors/internet"), api.get("/admin/settings")]);
        setItems(d.data); setSettings(s.data);
    };
    useEffect(() => { load(); }, []);

    const toggleVisibility = async (val) => {
        try {
            const { data } = await api.put("/admin/settings", { show_internet_doctors: val });
            setSettings(data); toast.success(`Internet doctors ${val ? "shown" : "hidden"} on the public site`);
        } catch (e) { toast.error("Failed"); }
    };

    const save = async () => {
        if (!form.name || !form.city) return toast.error("Name & city required");
        try {
            if (editing === "new") await api.post("/admin/doctors/internet", form);
            else await api.put(`/admin/doctors/internet/${editing}`, form);
            toast.success("Saved"); setEditing(null); load();
        } catch (e) { toast.error("Failed"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete?")) return;
        await api.delete(`/admin/doctors/internet/${id}`); load();
    };

    const [verifying, setVerifying] = useState(null);
    const verifyOne = async (id) => {
        try {
            await api.post(`/admin/doctors/internet/${id}/verify`);
            toast.success("Listing verified");
            setVerifying(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const seedStarter = async () => {
        if (!window.confirm("Add the curated starter dataset (~30 well-known clinics)? Existing entries will not be touched. Each row will be marked 'needs verification' for you to review.")) return;
        try {
            const { data } = await api.post("/admin/doctors/internet/seed-starter");
            toast.success(`Seed complete: ${data.inserted} new, ${data.skipped} already existed`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Only .xlsx files are supported"); e.target.value = ""; return;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/doctors/internet/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            toast.success(`Imported ${data.imported} listings${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
            if (data.errors?.length) console.warn("Import errors:", data.errors);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        } finally { e.target.value = ""; }
    };

    const startNew = () => { setEditing("new"); setForm(empty); };
    const startEdit = (d) => { setEditing(d.id); setForm({ ...empty, ...d }); };

    const filtered = items.filter((d) => {
        if (!filter.trim()) return true;
        const n = filter.toLowerCase();
        return (d.name + " " + d.city + " " + (d.area || "") + " " + (d.specialization || "")).toLowerCase().includes(n);
    });
    const needsVerifyCount = items.filter((d) => d.needs_verification).length;

    return (
        <div data-testid="admin-doctors-internet">
            <Toaster position="top-center" />
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Curated listings</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Internet-sourced doctors</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Listings of publicly-advertised homeopathy clinics. Toggle <strong>Show on public site</strong> to instantly hide the entire section from visitors.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <label className={`flex items-center gap-3 border px-4 py-2 ${settings.show_internet_doctors ? "border-sky-700 bg-sky-50" : "border-stone-300 bg-white"}`}>
                        <span className="text-sm">Show on public site</span>
                        <Switch checked={settings.show_internet_doctors} onCheckedChange={toggleVisibility} data-testid="toggle-internet-doctors" />
                    </label>
                </div>
            </div>

            {!settings.show_internet_doctors && (
                <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-4 text-sm text-amber-900 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>The Internet Doctors tab is currently <strong>hidden</strong> from the public site. Visitors will not see this section at all on the Doctors page until you turn it on again.</p>
                </div>
            )}

            <div className="bg-white border border-stone-200 p-3 mb-6 flex items-center justify-between flex-wrap gap-2">
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search name / city / area / speciality…" className="rounded-none border-stone-300 max-w-sm" data-testid="int-search" />
                <div className="flex flex-wrap items-center gap-2">
                    <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onUpload} data-testid="int-import-file" />
                    <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-none border-stone-400" data-testid="int-import"><Upload className="w-4 h-4 mr-2" />Bulk import (.xlsx)</Button>
                    <Button variant="outline" onClick={seedStarter} className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50" data-testid="int-seed"><Sparkles className="w-4 h-4 mr-2" />Add starter set</Button>
                    <Button onClick={startNew} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-internet-doctor"><Plus className="w-4 h-4 mr-2" />Add manually</Button>
                </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-4 text-xs text-amber-900">
                <strong>Excel format:</strong> first-row headers must include <code className="bg-white px-1">name</code> and <code className="bg-white px-1">city</code>. Optional: <code className="bg-white px-1">area</code>, <code className="bg-white px-1">state</code>, <code className="bg-white px-1">qualification</code>, <code className="bg-white px-1">specialization</code>, <code className="bg-white px-1">clinic_name</code>, <code className="bg-white px-1">clinic_address</code>, <code className="bg-white px-1">phone</code>, <code className="bg-white px-1">photo_url</code>, <code className="bg-white px-1">source_url</code>, <code className="bg-white px-1">experience_years</code>, <code className="bg-white px-1">rating</code>. All imported rows are flagged <em>needs verification</em>.
            </div>

            {needsVerifyCount > 0 && (
                <div className="text-xs text-stone-600 mb-3">
                    <strong>{needsVerifyCount}</strong> listing{needsVerifyCount === 1 ? "" : "s"} marked <em>needs verification</em>. Open each one, confirm details via the source URL, then turn off the flag.
                </div>
            )}

            {editing && (
                <div className="bg-white border border-stone-200 p-5 mb-6 space-y-3" data-testid="int-form">
                    <div className="flex justify-between"><h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} listing</h2><Button variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button></div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {["name", "qualification", "specialization", "city", "area", "state", "phone", "clinic_name", "source_url"].map(k => (
                            <div key={k}><Label className="text-xs uppercase tracking-widest text-stone-500">{k.replace(/_/g, " ")}</Label>
                                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid={`int-${k}`} /></div>
                        ))}
                        <div><Label className="text-xs uppercase tracking-widest text-stone-500">Experience years</Label>
                            <Input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value || 0, 10) })} className="rounded-none border-stone-300 mt-1" /></div>
                        <div><Label className="text-xs uppercase tracking-widest text-stone-500">Rating</Label>
                            <Input type="number" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value || 0) })} className="rounded-none border-stone-300 mt-1" /></div>
                    </div>
                    <ImageUploadInput
                        label="Doctor photo (optional)"
                        value={form.photo_url}
                        onChange={(v) => setForm({ ...form, photo_url: v })}
                        testId="int-photo"
                        multiple={false}
                        aspect="1/1"
                        hint="Headshot works best. Public site falls back to a placeholder if empty."
                    />
                    <Textarea value={form.clinic_address || ""} onChange={(e) => setForm({ ...form, clinic_address: e.target.value })} placeholder="Clinic address" className="rounded-none border-stone-300" />
                    <label className="flex items-center justify-between border border-stone-300 p-3"><span className="text-sm">Active (visible on public site)</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="int-active" /></label>
                    <label className="flex items-center justify-between border border-amber-300 bg-amber-50 p-3"><span className="text-sm">Needs verification</span><Switch checked={form.needs_verification} onCheckedChange={(v) => setForm({ ...form, needs_verification: v })} data-testid="int-needs-verify" /></label>
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="int-save">Save</Button>
                </div>
            )}

            <div className="bg-white border border-stone-200">
                {filtered.map((d) => (
                    <div key={d.id} className={`border-b border-stone-100 last:border-b-0 p-4 grid grid-cols-12 gap-3 items-center ${d.needs_verification ? "bg-amber-50/40" : ""}`} data-testid={`int-row-${d.id}`}>
                        <div className="col-span-3">
                            <div className="font-medium flex items-center gap-2">
                                {d.name}
                                {d.needs_verification && <span className="text-[9px] uppercase tracking-widest bg-amber-200 text-amber-900 px-1.5 py-0.5">verify</span>}
                            </div>
                            <div className="text-xs text-stone-500">{d.qualification}</div>
                        </div>
                        <div className="col-span-2 text-xs">{d.specialization}</div>
                        <div className="col-span-3 text-xs">
                            <div>{[d.area, d.city].filter(Boolean).join(", ")}</div>
                            <div className="text-stone-400">{d.state}</div>
                        </div>
                        <div className="col-span-1 text-xs">{d.experience_years}y · ★{d.rating}</div>
                        <div className="col-span-1 text-xs">{d.active ? <span className="text-sky-700">Active</span> : <span className="text-stone-400">Off</span>}</div>
                        <div className="col-span-2 flex gap-1 justify-end">
                            {d.needs_verification && (
                                <Button size="sm" variant="ghost" onClick={() => setVerifying(d)} className="rounded-none text-amber-700 hover:bg-amber-50" data-testid={`int-verify-${d.id}`} title="Verify listing">
                                    <ShieldCheck className="w-3.5 h-3.5" />
                                </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => startEdit(d)} className="rounded-none" data-testid={`int-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => del(d.id)} className="rounded-none text-red-700" data-testid={`int-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No listings yet. Click <strong>Add starter set</strong> to seed ~30 well-known clinics, or upload your own .xlsx.</div>}
            </div>

            {/* 1-click verify modal */}
            {verifying && (
                <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4" onClick={() => setVerifying(null)} data-testid="verify-modal">
                    <div className="bg-white max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b border-stone-200 p-4 flex items-center justify-between">
                            <div>
                                <div className="label-eyebrow text-sky-700">Verify listing</div>
                                <h2 className="font-serif text-2xl text-stone-900">{verifying.name}</h2>
                                <div className="text-xs text-stone-500 mt-1">{[verifying.area, verifying.city, verifying.state].filter(Boolean).join(" · ")}</div>
                            </div>
                            <Button variant="ghost" onClick={() => setVerifying(null)}><X className="w-4 h-4" /></Button>
                        </div>
                        <div className="p-4 grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2 text-sm">
                                <Field k="Qualification" v={verifying.qualification} />
                                <Field k="Specialization" v={verifying.specialization} />
                                <Field k="Clinic" v={verifying.clinic_name} />
                                <Field k="Phone" v={verifying.phone} />
                                <Field k="Address" v={verifying.clinic_address} />
                                <Field k="Experience" v={`${verifying.experience_years || 0} years`} />
                                <Field k="Rating" v={`★ ${verifying.rating}`} />
                                {verifying.source_url && (
                                    <div className="pt-3 border-t border-stone-200 mt-3">
                                        <a href={verifying.source_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:text-sky-800 underline inline-flex items-center text-xs" data-testid="verify-open-source">
                                            Open source <ExternalLink className="w-3 h-3 ml-1" />
                                        </a>
                                        <p className="text-[10px] text-stone-500 mt-1">Verify the contact details on the source page, then click Confirm.</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                {verifying.source_url ? (
                                    <iframe
                                        title="source preview"
                                        src={verifying.source_url}
                                        className="w-full h-72 border border-stone-200"
                                        sandbox="allow-scripts allow-same-origin"
                                    />
                                ) : (
                                    <div className="border border-dashed border-stone-300 h-72 flex items-center justify-center text-xs text-stone-400 p-4 text-center">
                                        No source URL on this listing. Open the edit form to add one, or just confirm if you've verified it elsewhere.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="border-t border-stone-200 p-4 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => { setVerifying(null); startEdit(verifying); }} className="rounded-none">Edit details first</Button>
                            <Button onClick={() => verifyOne(verifying.id)} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="verify-confirm">
                                <ShieldCheck className="w-4 h-4 mr-2" />Confirm — listing is correct
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Field = ({ k, v }) => (
    <div className="grid grid-cols-3 gap-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 col-span-1">{k}</div>
        <div className="col-span-2 text-stone-800 break-words">{v || <span className="text-stone-400">—</span>}</div>
    </div>
);
